export interface VideoTarget {
  src: string
  loop: boolean
  onEnded?: () => void
}

interface CrossfaderOptions {
  fadeMs?: number
  onLoadError?: (fatal: boolean, src: string) => void
  scheduleFrame?: (callback: FrameRequestCallback) => number
  cancelFrame?: (handle: number) => void
  scheduleTimeout?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>
  cancelTimeout?: (handle: ReturnType<typeof setTimeout>) => void
}

export interface CrossfaderSnapshot {
  activeIndex: 0 | 1
  activeSrc: string | null
  targetSrc: string | null
}

const defaultScheduleFrame = (callback: FrameRequestCallback): number => {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(callback)
  return setTimeout(() => callback(performance.now()), 0) as unknown as number
}

const defaultCancelFrame = (handle: number): void => {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle)
  else clearTimeout(handle)
}

/**
 * A/B video player that never clears the visible panel while the next clip is
 * loading. The incoming panel is decoded off-screen and only crossfades after
 * the browser reports actual playback progress.
 */
export class DualVideoCrossfader {
  private readonly videos: readonly [HTMLVideoElement, HTMLVideoElement]
  private readonly fadeMs: number
  private readonly onLoadError?: (fatal: boolean, src: string) => void
  private readonly scheduleFrame: (callback: FrameRequestCallback) => number
  private readonly cancelFrame: (handle: number) => void
  private readonly scheduleTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>
  private readonly cancelTimeout: (handle: ReturnType<typeof setTimeout>) => void

  private activeIndex: 0 | 1 = 0
  private activeSrc: string | null = null
  private targetSrc: string | null = null
  private generation = 0
  private pendingCleanup: (() => void) | null = null
  private endedCleanup: (() => void) | null = null
  private pauseTimer: ReturnType<typeof setTimeout> | null = null

  constructor(videos: readonly [HTMLVideoElement, HTMLVideoElement], options: CrossfaderOptions = {}) {
    this.videos = videos
    this.fadeMs = options.fadeMs ?? 220
    this.onLoadError = options.onLoadError
    this.scheduleFrame = options.scheduleFrame ?? defaultScheduleFrame
    this.cancelFrame = options.cancelFrame ?? defaultCancelFrame
    this.scheduleTimeout = options.scheduleTimeout ?? ((callback, delayMs) => setTimeout(callback, delayMs))
    this.cancelTimeout = options.cancelTimeout ?? ((handle) => clearTimeout(handle))

    for (const video of videos) {
      video.style.opacity = '0'
      video.style.transition = 'none'
      video.style.zIndex = '1'
    }
  }

  getSnapshot(): CrossfaderSnapshot {
    return { activeIndex: this.activeIndex, activeSrc: this.activeSrc, targetSrc: this.targetSrc }
  }

  setTarget(target: VideoTarget): void {
    if (this.targetSrc === target.src) {
      const active = this.videos[this.activeIndex]
      if (this.activeSrc === target.src) void active.play().catch(() => undefined)
      return
    }

    this.generation += 1
    const generation = this.generation
    this.clearPending()
    this.clearEnded()
    this.clearPauseTimer()

    const hasActivePanel = this.activeSrc != null
    const incomingIndex: 0 | 1 = hasActivePanel ? (this.activeIndex === 0 ? 1 : 0) : this.activeIndex
    const incoming = this.videos[incomingIndex]
    const outgoing = hasActivePanel ? this.videos[this.activeIndex] : null
    this.targetSrc = target.src

    incoming.pause()
    incoming.style.transition = 'none'
    incoming.style.opacity = '0'
    incoming.style.zIndex = '2'
    if (outgoing) {
      outgoing.style.transition = 'none'
      outgoing.style.opacity = '1'
      outgoing.style.zIndex = '1'
    }

    incoming.muted = true
    incoming.playsInline = true
    incoming.loop = target.loop
    incoming.src = target.src

    let frameOne: number | null = null
    let frameTwo: number | null = null
    let committed = false
    let retryOnCanPlay: (() => void) | null = null

    const cleanupListeners = () => {
      incoming.removeEventListener('playing', onPlayableFrame)
      incoming.removeEventListener('timeupdate', onPlayableFrame)
      incoming.removeEventListener('error', onError)
      if (retryOnCanPlay) incoming.removeEventListener('canplay', retryOnCanPlay)
      retryOnCanPlay = null
      if (frameOne != null) this.cancelFrame(frameOne)
      if (frameTwo != null) this.cancelFrame(frameTwo)
      frameOne = null
      frameTwo = null
    }

    const commit = () => {
      if (committed || generation !== this.generation) return
      committed = true
      cleanupListeners()

      incoming.style.transition = `opacity ${this.fadeMs}ms linear`
      incoming.style.opacity = '1'
      if (outgoing) {
        outgoing.style.transition = `opacity ${this.fadeMs}ms linear`
        outgoing.style.opacity = '0'
      }

      this.activeIndex = incomingIndex
      this.activeSrc = target.src
      this.targetSrc = target.src
      this.pendingCleanup = null

      if (target.onEnded) {
        const onEnded = () => target.onEnded?.()
        incoming.addEventListener('ended', onEnded, { once: true })
        this.endedCleanup = () => incoming.removeEventListener('ended', onEnded)
      }

      if (outgoing) {
        this.pauseTimer = this.scheduleTimeout(() => {
          if (generation === this.generation) outgoing.pause()
          this.pauseTimer = null
        }, this.fadeMs + 40)
      }
    }

    const onPlayableFrame = () => {
      if (committed || frameOne != null || generation !== this.generation) return
      // Two paints ensure the decoded frame reached the compositor before the
      // opacity transition starts. Until then the old panel remains at 1.
      frameOne = this.scheduleFrame(() => {
        frameOne = null
        frameTwo = this.scheduleFrame(() => {
          frameTwo = null
          commit()
        })
      })
    }

    const onError = () => {
      if (generation !== this.generation) return
      cleanupListeners()
      incoming.pause()
      incoming.style.opacity = '0'
      this.targetSrc = this.activeSrc
      this.pendingCleanup = null
      this.onLoadError?.(!hasActivePanel, target.src)
    }

    incoming.addEventListener('playing', onPlayableFrame)
    incoming.addEventListener('timeupdate', onPlayableFrame)
    incoming.addEventListener('error', onError, { once: true })
    this.pendingCleanup = cleanupListeners
    incoming.load()
    void incoming.play().catch(() => {
      if (generation !== this.generation || committed) return
      retryOnCanPlay = () => {
        if (retryOnCanPlay) incoming.removeEventListener('canplay', retryOnCanPlay)
        retryOnCanPlay = null
        if (generation === this.generation) void incoming.play().catch(() => undefined)
      }
      incoming.addEventListener('canplay', retryOnCanPlay, { once: true })
    })
  }

  stop(): void {
    this.generation += 1
    this.clearPending()
    this.clearEnded()
    this.clearPauseTimer()
    this.activeSrc = null
    this.targetSrc = null
    for (const video of this.videos) {
      video.pause()
      video.style.transition = 'none'
      video.style.opacity = '0'
    }
  }

  destroy(): void {
    this.stop()
  }

  private clearPending(): void {
    this.pendingCleanup?.()
    this.pendingCleanup = null
  }

  private clearEnded(): void {
    this.endedCleanup?.()
    this.endedCleanup = null
  }

  private clearPauseTimer(): void {
    if (this.pauseTimer == null) return
    this.cancelTimeout(this.pauseTimer)
    this.pauseTimer = null
  }
}
